import { inject, Injectable } from '@angular/core';
import { delay, Observable, of, switchMap } from 'rxjs';
import { Ticket } from 'src/app/core/entities/ticket/domain/model/ticket.model';
import { TicketMapper } from 'src/app/core/entities/ticket/infrastructure/mapper/ticket.mapper';
import { Logger } from 'src/app/core/interfaces/Logger';
import {
  IApiTicket,
  IApiTicketGetAllQueryParams,
  IApiTicketGetBySearchQueryParams,
} from '../interfaces/ticket-api.interface';
import { ticketApiMock1, ticketApiMocks } from '../mocks/ticket.mock';
import { TicketFactoryService } from './ticket-factory.service';

@Injectable({ providedIn: 'root' })
export class TicketMockService {
  private readonly ticketFactoryService = inject(TicketFactoryService);
  private readonly logger = inject(Logger);
  private readonly delayTime = this.getRandomDelay();

  getAll(params?: IApiTicketGetAllQueryParams): Observable<Ticket[]> {
    this.logOperation(
      'getAll',
      `Creating fresh mock ticket instances ${params}`
    );

    return this.createDelayedResponse(ticketApiMocks.map(this.toTicket));
  }

  getById(id: string): Observable<Ticket | undefined> {
    this.logOperation('getById', `Getting ticket by id ${id}`);

    return this.createDelayedResponse(ticketApiMock1).pipe(
      switchMap((ticket) => this.ticketFactoryService.createOne(ticket, true))
    );
  }

  getBySearch(params?: IApiTicketGetBySearchQueryParams): Observable<Ticket[]> {
    this.logOperation(
      'getBySearch',
      `Getting tickets by search params ${params}`
    );

    return this.createDelayedResponse(ticketApiMocks.map(this.toTicket));
  }

  getByIds(ids: string[]): Observable<Ticket[]> {
    this.logOperation('getByIds', `Getting tickets by ids ${ids}`);

    if (ids.length === 0) {
      return of([]);
    }

    return this.createDelayedResponse(
      ticketApiMocks
        .filter((ticket) => ids.includes(ticket._id))
        .map(this.toTicket)
    );
  }

  private getRandomDelay(): number {
    return Math.random() * 1000 + 500;
  }

  private createDelayedResponse<T>(data: T): Observable<T> {
    return of(data).pipe(delay(this.delayTime));
  }

  private toTicket = (mockTicket: IApiTicket): Ticket => {
    return TicketMapper.fromJSON(mockTicket);
  };

  private logOperation(operation: string, message: string): void {
    this.logger.debug(`[BookingApiMockAdapter][${operation}] ${message}`);
  }
}
